import { useQueryClient } from '@tanstack/react-query';
import { EApiRoutes } from 'api/api.utils';
import usePortalSettings from 'api/portal/usePortalSettings';
import { useUserDetails } from 'api/useUserDetails';
import { Portal } from 'common/contracts';
import { useFormik } from 'formik';
import { EToasterStatus, showToast } from 'utils/showToast';

import { toastMessages } from '../DeveloperSettings.constants';
import { portalInfoSchema } from '../DeveloperSettings.schemas';
import { ESavingForm } from '../DeveloperSettings.types';

interface IUsePortalInfoFormParams {
  portalData: Portal | undefined;
  portalId: string | undefined;
  setSavingForm: (form: ESavingForm) => void;
}

export const usePortalInfoForm = ({
  portalData,
  portalId,
  setSavingForm
}: IUsePortalInfoFormParams) => {
  const { updateBasicPortalSettings } = usePortalSettings();
  const { userDetails } = useUserDetails();
  const queryClient = useQueryClient();

  return useFormik({
    initialValues: {
      portalName: portalData?.portalName || '',
      domain: portalData?.portalDomain || ''
    },
    validationSchema: portalInfoSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setSavingForm(ESavingForm.PORTAL_INFO);
      try {
        await updateBasicPortalSettings.mutateAsync({
          portalName: values.portalName,
          portalDomain: values.domain
        });

        if (userDetails) {
          const updatedPortals = userDetails.portals?.map((portal: Portal) => {
            if (portal.portalId === portalId) {
              return {
                ...portal,
                portalName: values.portalName,
                portalDomain: values.domain
              };
            }
            return portal;
          });

          queryClient.setQueryData([EApiRoutes.USERS], {
            ...userDetails,
            portals: updatedPortals
          });
        }

        showToast({
          message: toastMessages.portalInfoSuccess,
          status: EToasterStatus.SUCCESS
        });
        setSavingForm(ESavingForm.NONE);
      } catch (error: unknown) {
        const errorMessage = (error as { response?: { data?: { message?: string } } })
          ?.response?.data?.message;
        showToast({
          message: errorMessage || toastMessages.portalInfoError,
          status: EToasterStatus.ERROR
        });
        setSavingForm(ESavingForm.NONE);
      }
    }
  });
};
